// Feature 2 — Mid-term programme upgrade modal.
//
// Surfaced from the PSAC enrolment list (admission detail surface) for any
// ACTIVE SCHOOL admission. Lets ops change the programme variant, age-group
// standard, batch, and effective date in one shot; computes the differential
// locally as ops fills the form so they see the math before submitting;
// submits to POST /api/v1/oh_users/student_admission/<uuid>/modify/.
//
// Permissions:
//   - The "Override downgrade" checkbox only renders for users in
//     AppConfig['DISCOUNT_AUTHORIZED_PHONE_NUMBERS'] (Feature 4 whitelist).
//     Server re-checks on submit, so this is a UI affordance only.
//
// Live-preview math mirrors Feature 1's calculate_preschool_first_payment:
//   - Month 1 of a term (any day) → 4 × (new - old).
//   - Months 2-3 day 1-15 → (5 - month_position) × (new - old).
//   - Months 2-3 day 16-end → (4.5 - month_position) × (new - old).
//   - Month 4 day 1-15 → 1 × (new - old).
//   - Month 4 day 16-end → 0.5 × (new - old).
// Backend is the source of truth; preview can be off by ±₹1 due to GST
// rounding but the order-of-magnitude is what ops need.

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import FileCopyIcon from '@material-ui/icons/FileCopy'
import dayjs from 'dayjs'
import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import React from 'react'

import {
  fetchCanApplyCustomDiscount,
  generatePSACTermInvoice,
  getProgramBatches,
  getProgramFee,
  modifyPSACAdmission,
  previewPSACAdmissionModification,
  previewPSACTermInvoice,
} from 'api'
import ProgramRupees from 'app/pages/Class/Inventory/components/ModalsAndActions/components/CheckoutModal/components/ProgramRupees'
import constants from 'app/constants'
import { eventAlert } from 'app/helpers'

const useStyles = makeStyles((theme) => ({
  dialog: {
    minWidth: 560
  },
  row: {
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2)
  },
  field: {
    flex: 1
  },
  previewPane: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: 4,
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    fontSize: 12,
    lineHeight: 1.5
  },
  paymentLinkBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.success.main}`,
    borderRadius: 4
  },
  paymentLinkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1)
  }
}))

const computePreviewBreakdown = ({
  currentMonthly,
  newMonthly,
  effectiveDate,
  termStart,
  termEnd
}) => {
  if (!effectiveDate || !termStart || !termEnd || newMonthly == null || currentMonthly == null) {
    return null
  }
  const d = dayjs(effectiveDate)
  const ts = dayjs(termStart)
  const te = dayjs(termEnd)
  if (!d.isValid() || !ts.isValid() || !te.isValid()) return null
  if (d.isBefore(ts) || d.isAfter(te)) return null

  const monthPosition = (d.year() - ts.year()) * 12 + (d.month() - ts.month()) + 1
  const termMonths = (te.year() - ts.year()) * 12 + (te.month() - ts.month()) + 1
  const isFirstMonth = monthPosition === 1
  const isLastMonth = monthPosition === termMonths
  const isPost15 = d.date() > 15

  let factor
  if (isFirstMonth) {
    factor = termMonths
  } else if (isLastMonth) {
    factor = isPost15 ? 0.5 : 1.0
  } else {
    const monthsRemaining = termMonths - monthPosition + 1
    const joinMonthFactor = isPost15 ? 0.5 : 1.0
    factor = joinMonthFactor + (monthsRemaining - 1)
  }

  const delta = newMonthly - currentMonthly
  const differential = factor * delta
  return {
    factor,
    delta,
    differential,
    monthPosition,
    termMonths,
    isPost15
  }
}

const ModifyAdmissionModal = (props) => {
  const classes = useStyles()
  const { open, admission, onClose, onSuccess } = props

  const [programFees, setProgramFees] = React.useState([])
  const [batches, setBatches] = React.useState([])
  const [selectedProgramFeeUuid, setSelectedProgramFeeUuid] = React.useState('')
  const [selectedBatchUuid, setSelectedBatchUuid] = React.useState('')
  const [selectedStandardUuid, setSelectedStandardUuid] = React.useState('')
  const [effectiveDate, setEffectiveDate] = React.useState(
    dayjs().format('YYYY-MM-DD')
  )
  const [overrideDowngrade, setOverrideDowngrade] = React.useState(false)
  // Charmi 2026-05-28 item 2.2 — pay-with-GST toggle for daycare-
  // reimbursement parents. Forces the full differential to be taxable
  // regardless of the admission's stored is_daycare_billing.
  const [payWithGst, setPayWithGst] = React.useState(false)
  const [canOverride, setCanOverride] = React.useState(false)
  const [submitting, setSubmitting] = React.useState(false)
  const [submitError, setSubmitError] = React.useState(null)
  const [result, setResult] = React.useState(null)
  // Akshay 2026-05-27 — Phase 2. Two-step submit: form → preview
  // summary (cart-style) → generate-link. `previewData` is set after
  // the preview API call returns; when non-null, the dialog renders
  // the payment summary + Generate / Back buttons instead of the form.
  const [previewData, setPreviewData] = React.useState(null)
  const [previewing, setPreviewing] = React.useState(false)

  const currentProgramName = admission?.program_fee?.program?.name
  const currentStandardName = admission?.program_enrolment?.student?.standard?.name
  const currentBatchName = admission?.program_enrolment?.batch?.name
  const currentMonthly = admission?.program_fee?.monthly_pretax_amount
  const centreUuid = admission?.program_enrolment?.batch?.centre?.uuid

  // Charmi 2026-05-28 item 2.1 — a seat-blocked admission (active, AIM
  // paid, Term 1 still PENDING) that changes programme at this point
  // should be billed the FULL new-programme term fee via the term-fee
  // flow, not the modify-admission differential. Same detection as
  // PSACEnrolment.isSeatBlockRow.
  const isSeatBlock = React.useMemo(() => {
    if (!admission || admission.status !== 'active' || !admission.admission_fee_paid) {
      return false
    }
    const first = (admission.student_admission_installments || []).find(
      (i) => i.sequence === 1
    )
    return Boolean(first) && first.status === 'pending'
  }, [admission])

  React.useEffect(() => {
    if (!open) return
    fetchCanApplyCustomDiscount().then(setCanOverride)
  }, [open])

  // Load program-fees for the centre.
  React.useEffect(() => {
    if (!open || !centreUuid) return
    ;(async () => {
      try {
        const response = await getProgramFee({
          queries: {
            centre_uuid: centreUuid,
            status: 'active'
          }
        })
        const rows = response?.data?.results || []
        setProgramFees(rows)
      } catch (e) {
        console.error('Error fetching program fees', e)
      }
    })()
  }, [open, centreUuid])

  // Akshay (2026-05-27) — Modify Admission UX rework. Order is now
  // age-group (standard) → program → batch (was program → standard →
  // batch). Rationale: ops most often models movement BETWEEN age
  // groups (Toddler → Pre-nursery, etc.) and within each age group
  // the programme options are a fixed set (Half Day, Extended, Full
  // Day; plus 2 Hour for Toddler).
  const ALLOWED_PROGRAM_NAMES_BASE = [
    'Preschool Half Day',
    'Preschool Extended',
    'Preschool Full Day',
  ]
  const TODDLER_EXTRA_PROGRAMS = ['Preschool 2 Hour']
  const TODDLER_SHORT_CODES = new Set(['TODLR', 'TODDLR', 'TOD'])

  // All distinct standards across the centre's ProgramFees. Standards
  // are flattened from each PF's `.standards` array and deduped by uuid.
  // Age groups appear in age order (sort by `.age` when present, fall
  // back to name).
  const standardOptions = React.useMemo(() => {
    const seen = new Map()
    for (const pf of programFees) {
      for (const s of pf?.standards || []) {
        if (!s?.uuid || seen.has(s.uuid)) continue
        seen.set(s.uuid, s)
      }
    }
    const list = Array.from(seen.values())
    list.sort((a, b) => {
      const aa = a?.age == null ? 99 : a.age
      const bb = b?.age == null ? 99 : b.age
      if (aa !== bb) return aa - bb
      return (a?.name || '').localeCompare(b?.name || '')
    })
    return list
  }, [programFees])

  // Programs available for the picked standard. PFs whose `.standards`
  // include the selected standard contribute. Filtered to the allowed
  // whitelist (Half Day / Extended / Full Day, plus 2 Hour for Toddler).
  //
  // Akshay 2026-05-27 — restrict to the SAME academic year as the
  // current admission so the dropdown shows the price tier the
  // admission is actually on. Without this, centres with legacy AYs
  // (e.g. 2025-26 at ₹16,500/mo and 2026-27 at ₹18,000/mo) surfaced
  // the stale lower-priced PF as the "cheapest" — wrong since the
  // modify must stay within the admission's own AY.
  const currentAcademicYearUuid = admission?.program_fee?.academic_year?.uuid
  const programOptions = React.useMemo(() => {
    if (!selectedStandardUuid) return []
    const selectedStandard = standardOptions.find(
      (s) => s.uuid === selectedStandardUuid
    )
    const isToddler = TODDLER_SHORT_CODES.has(selectedStandard?.short_code)
    const whitelist = new Set(
      isToddler
        ? [...ALLOWED_PROGRAM_NAMES_BASE, ...TODDLER_EXTRA_PROGRAMS]
        : ALLOWED_PROGRAM_NAMES_BASE
    )

    const byProgramName = new Map() // name → { name, programUuid, pf }
    for (const pf of programFees) {
      const name = pf?.program?.name
      if (!name || !whitelist.has(name)) continue
      // Restrict to the admission's AY when we know it; otherwise fall
      // back to all-AYs (defensive — admission detail should always
      // expose academic_year).
      if (
        currentAcademicYearUuid &&
        pf?.academic_year?.uuid !== currentAcademicYearUuid
      ) {
        continue
      }
      const covers = (pf?.standards || []).some(
        (s) => s?.uuid === selectedStandardUuid
      )
      if (!covers) continue
      const existing = byProgramName.get(name)
      if (
        !existing ||
        (pf.monthly_pretax_amount || 0) <
          (existing.cheapestPf.monthly_pretax_amount || 0)
      ) {
        byProgramName.set(name, {
          name,
          programUuid: pf.program.uuid,
          cheapestPf: pf,
        })
      }
    }

    // Preserve the whitelist order (Half Day → Extended → Full Day →
    // 2 Hour) rather than insertion order from programFees.
    const orderedNames = isToddler
      ? [...ALLOWED_PROGRAM_NAMES_BASE, ...TODDLER_EXTRA_PROGRAMS]
      : ALLOWED_PROGRAM_NAMES_BASE
    return orderedNames
      .filter((n) => byProgramName.has(n))
      .map((n) => byProgramName.get(n))
  }, [programFees, selectedStandardUuid, standardOptions, currentAcademicYearUuid])

  const selectedProgramFee = React.useMemo(
    () => programFees.find((pf) => pf.uuid === selectedProgramFeeUuid),
    [programFees, selectedProgramFeeUuid]
  )

  // When the user picks a program (by program uuid) we resolve that to
  // the cheapest ProgramFee in the picked-standard list, then commit
  // both selectedProgramFeeUuid (used for submit) + the surface program
  // uuid (used as the Select's value).
  const selectedProgramUuid = selectedProgramFee?.program?.uuid || ''

  // Filter batches by centre + chosen program + chosen standard (vacancy > 0).
  React.useEffect(() => {
    if (!open || !selectedProgramFee || !selectedStandardUuid) {
      setBatches([])
      return
    }
    ;(async () => {
      try {
        // Charmi #9 (2026-05-25) follow-up — BatchFilter uses *_uuid suffixes
        // (centre_uuid, program_uuid, standards_uuid). The previous param
        // names (centre, program, standard) silently matched zero rows
        // because `centre` does icontains-on-name (we were passing a UUID
        // string) and `program`/`standard` aren't declared at all.
        const response = await getProgramBatches({
          queries: {
            centre_uuid: centreUuid,
            program_uuid: selectedProgramFee.program?.uuid,
            standards_uuid: selectedStandardUuid,
            status: 'active'
          }
        })
        const rows = response?.data?.results || []
        // Ordered by vacancy desc, then start_date.
        const sorted = [...rows].sort((a, b) => {
          const av = a?.vacancy ?? 0
          const bv = b?.vacancy ?? 0
          if (bv !== av) return bv - av
          return (a?.start_date || '').localeCompare(b?.start_date || '')
        })
        setBatches(sorted.filter((b) => (b?.vacancy ?? 0) > 0))
      } catch (e) {
        console.error('Error fetching batches', e)
      }
    })()
  }, [open, selectedProgramFee, selectedStandardUuid, centreUuid])

  // Current term boundaries — derived from the chosen program fee's
  // payment_installments. Fall back to AY end date as a safety net.
  const currentTerm = React.useMemo(() => {
    if (!selectedProgramFee || !effectiveDate) return null
    const installments = selectedProgramFee.payment_installments || []
    const d = dayjs(effectiveDate)
    for (const inst of installments) {
      const ap = inst?.academic_period
      if (!ap) continue
      if (
        dayjs(ap.start_date).isSame(d, 'day') ||
        dayjs(ap.end_date).isSame(d, 'day') ||
        (dayjs(ap.start_date).isBefore(d) && dayjs(ap.end_date).isAfter(d))
      ) {
        return ap
      }
    }
    return null
  }, [selectedProgramFee, effectiveDate])

  const preview = React.useMemo(() => {
    const newMonthly = selectedProgramFee?.monthly_pretax_amount
    return computePreviewBreakdown({
      currentMonthly,
      newMonthly,
      effectiveDate,
      termStart: currentTerm?.start_date,
      termEnd: currentTerm?.end_date
    })
  }, [
    currentMonthly,
    selectedProgramFee,
    effectiveDate,
    currentTerm
  ])

  const isDowngrade =
    selectedProgramFee?.monthly_pretax_amount != null &&
    currentMonthly != null &&
    selectedProgramFee.monthly_pretax_amount < currentMonthly

  // Phase 2 — step 1: preview. Hits the BE preview endpoint and stashes
  // the cart-style payment_summary. No invoice is created yet.
  const handlePreview = async () => {
    setPreviewing(true)
    setSubmitError(null)
    try {
      const data = isSeatBlock
        ? await previewPSACTermInvoice({
            studentAdmissionUuid: admission.uuid,
            newProgramFeeUuid: selectedProgramFeeUuid,
            newBatchUuid: selectedBatchUuid,
            forceGst: payWithGst
          })
        : await previewPSACAdmissionModification({
            studentAdmissionUuid: admission.uuid,
            newProgramFeeUuid: selectedProgramFeeUuid,
            newBatchUuid: selectedBatchUuid,
            effectiveDate,
            overrideDowngrade: overrideDowngrade && canOverride,
            forceGst: payWithGst
          })
      setPreviewData(data)
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Preview failed'
      setSubmitError(message)
      eventAlert(message, constants.ERROR)
    } finally {
      setPreviewing(false)
    }
  }

  // Phase 2 — step 2: commit. After ops reviews the summary, this call
  // creates the differential Invoice + Razorpay link. (Per Phase 1, no
  // admission mutation happens here — that's deferred until payment.)
  const handleSubmit = async () => {
    setSubmitting(true)
    setSubmitError(null)
    try {
      const data = isSeatBlock
        ? await generatePSACTermInvoice({
            studentAdmissionUuid: admission.uuid,
            newProgramFeeUuid: selectedProgramFeeUuid,
            newBatchUuid: selectedBatchUuid,
            effectiveDate,
            forceGst: payWithGst
          })
        : await modifyPSACAdmission({
            studentAdmissionUuid: admission.uuid,
            newProgramFeeUuid: selectedProgramFeeUuid,
            newBatchUuid: selectedBatchUuid,
            effectiveDate,
            overrideDowngrade: overrideDowngrade && canOverride,
            forceGst: payWithGst
          })
      setResult(data)
      eventAlert(
        isSeatBlock
          ? 'Term fee invoice generated — share the payment link with the parent.'
          : 'Payment link generated — share with the parent. The admission ' +
              'will be modified once payment is received.',
        constants.SUCCESS
      )
      if (onSuccess) onSuccess(data)
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Modification failed'
      setSubmitError(message)
      eventAlert(message, constants.ERROR)
    } finally {
      setSubmitting(false)
    }
  }

  const copyToClipboard = (text) => {
    if (!text) return
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text)
    }
    eventAlert('Payment link copied.', constants.SUCCESS)
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>Modify admission</DialogTitle>
      <DialogContent className={classes.dialog}>
        <Typography variant='body2' color='textSecondary'>
          Current: {currentProgramName || '—'},{' '}
          {currentStandardName || '—'},{' '}
          {currentBatchName || '—'} (₹{Math.round(currentMonthly || 0)}/month)
        </Typography>

        {/* Akshay (2026-05-27) — flipped order: age-group → program → batch.
            Form is hidden once the preview API returns (replaced by the cart-
            style payment summary), and again hidden once the differential
            invoice + Razorpay link are created. */}
        {!previewData && !result && (
        <>
        <Box className={classes.row}>
          <FormControl className={classes.field} variant='outlined' size='small'>
            <InputLabel>New age group</InputLabel>
            <Select
              label='New age group'
              value={selectedStandardUuid}
              onChange={(e) => {
                setSelectedStandardUuid(e.target.value)
                setSelectedProgramFeeUuid('')
                setSelectedBatchUuid('')
              }}
            >
              {standardOptions.map((s) => (
                <MenuItem key={s.uuid} value={s.uuid}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box className={classes.row}>
          <FormControl
            className={classes.field}
            variant='outlined'
            size='small'
            disabled={!selectedStandardUuid}
          >
            <InputLabel>New program</InputLabel>
            <Select
              label='New program'
              value={selectedProgramUuid}
              onChange={(e) => {
                const pickedProgramUuid = e.target.value
                const match = programOptions.find(
                  (p) => p.programUuid === pickedProgramUuid
                )
                setSelectedProgramFeeUuid(match?.cheapestPf?.uuid || '')
                setSelectedBatchUuid('')
              }}
            >
              {programOptions.length === 0 ? (
                <MenuItem value='' disabled>
                  {selectedStandardUuid
                    ? 'No programmes available for this age group'
                    : 'Pick an age group first'}
                </MenuItem>
              ) : (
                programOptions.map((p) => (
                  <MenuItem key={p.programUuid} value={p.programUuid}>
                    {p.name} — ₹
                    {Math.round(p.cheapestPf.monthly_pretax_amount || 0)}/mo
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          <FormControl className={classes.field} variant='outlined' size='small' disabled={!selectedStandardUuid}>
            <InputLabel>New batch</InputLabel>
            <Select
              label='New batch'
              value={selectedBatchUuid}
              onChange={(e) => setSelectedBatchUuid(e.target.value)}
            >
              {batches.length === 0 ? (
                // Charmi #9 (2026-05-25) — surface the empty state instead
                // of a blank dropdown so ops can tell "no matching batches"
                // from "still loading" / "select standard first".
                <MenuItem value='' disabled>
                  {selectedStandardUuid
                    ? 'No batches with vacancy for this program + standard'
                    : 'Select a program and standard first'}
                </MenuItem>
              ) : (
                batches.map((b) => {
                  // Charmi modify-admission test (2026-05-26) — Batch.name
                  // is null on many production batches (e.g. legacy seeded
                  // preschool batches), so the previous render output was
                  // literally " (vacancy N)" with no identifying info. Build
                  // a useful label from subject + teacher + cadence as the
                  // fallback, mirroring how the Enrolments table surfaces
                  // batches in its own columns.
                  const subjectName =
                    b?.course?.subjects?.[0]?.name || b?.course?.name
                  const teacherName = b?.teacher?.full_name
                  const cadence0 = b?.cadence?.[0]
                  const timing = cadence0
                    ? `${cadence0.start_time?.replace('-', ':')}-${cadence0.end_time?.replace('-', ':')}`
                    : null
                  const label =
                    b.name ||
                    [subjectName, teacherName ? `(${teacherName})` : null, timing]
                      .filter(Boolean)
                      .join(' ') ||
                    `Batch ${b.uuid?.slice(0, 8)}`
                  return (
                    <MenuItem key={b.uuid} value={b.uuid}>
                      {label} (vacancy {b.vacancy ?? 0})
                    </MenuItem>
                  )
                })
              )}
            </Select>
          </FormControl>
        </Box>

        <Box className={classes.row}>
          <TextField
            className={classes.field}
            label='Effective date'
            type='date'
            size='small'
            variant='outlined'
            InputLabelProps={{ shrink: true }}
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            inputProps={{
              min: dayjs().format('YYYY-MM-DD'),
              max: currentTerm?.end_date
            }}
          />
        </Box>

        {isDowngrade && canOverride && (
          <Box className={classes.row}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={overrideDowngrade}
                  onChange={(e) => setOverrideDowngrade(e.target.checked)}
                />
              }
              label='Override downgrade (authorised users only)'
            />
          </Box>
        )}

        <Box className={classes.row}>
          <FormControlLabel
            control={
              <Checkbox
                checked={payWithGst}
                onChange={(e) => {
                  setPayWithGst(e.target.checked)
                  setPreviewData(null)
                }}
              />
            }
            label='Pay with GST (full amount taxable — for daycare reimbursement)'
          />
        </Box>

        </>
        )}

        {/* Phase 2: cart-style payment summary. Rendered after the user
            clicks Preview and the BE returns the differential breakdown. */}
        {previewData && !result && (
          <ProgramRupees summary={previewData.payment_summary} />
        )}

        {submitError && (
          <Typography color='error' variant='body2' style={{ marginTop: 16 }}>
            {submitError}
          </Typography>
        )}

        {result?.payment_link && (
          <Box className={classes.paymentLinkBox}>
            <Typography variant='subtitle2'>
              Differential invoice created. Share this link with the parent:
            </Typography>
            <div className={classes.paymentLinkRow}>
              <Typography variant='body2' style={{ wordBreak: 'break-all' }}>
                {result.payment_link}
              </Typography>
              <IconButton
                size='small'
                onClick={() => copyToClipboard(result.payment_link)}
                title='Copy link'
              >
                <FileCopyIcon fontSize='small' />
              </IconButton>
            </div>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {result ? (
          <Button onClick={onClose}>Close</Button>
        ) : previewData ? (
          <>
            <Button
              onClick={() => setPreviewData(null)}
              disabled={submitting}
            >
              Back to edit
            </Button>
            <Button
              onClick={handleSubmit}
              color='primary'
              variant='contained'
              disabled={submitting}
            >
              {submitting ? 'Generating link…' : 'Generate payment link'}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={previewing}>
              Cancel
            </Button>
            <Button
              onClick={handlePreview}
              color='primary'
              variant='contained'
              disabled={
                previewing ||
                !selectedProgramFeeUuid ||
                !selectedBatchUuid ||
                !effectiveDate ||
                (isDowngrade && !overrideDowngrade)
              }
            >
              {previewing ? 'Loading preview…' : 'Preview'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}

ModifyAdmissionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  admission: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func
}

export default ModifyAdmissionModal
